import {Component, Input, OnDestroy, OnInit, HostListener, ElementRef, ChangeDetectionStrategy} from '@angular/core';
import {User} from '../../models/user';
import {ConnectedPosition, CdkConnectedOverlay, CdkOverlayOrigin} from '@angular/cdk/overlay';
import {DropdownManagerService} from '../../services/dropdown-manager.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'motn-user-avatar',
  templateUrl: './user-avatar.component.html',
  styleUrls: ['./user-avatar.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CdkConnectedOverlay, CdkOverlayOrigin],
})
export class UserAvatarComponent implements OnInit, OnDestroy {
  @Input() userIcon: string = 'fa-user';
  @Input() user: User | null = null;
  @Input() size: 'small' | 'medium' | 'large' = 'medium';
  @Input() showName: boolean = false;
  @Input() showMotiLabel: boolean = false;
  @Input() clickable: boolean = false;
  @Input() rtl: boolean = false;
  @Input() avatarAfterName: boolean = false;

  dropdownOpen: boolean = false;
  private dropdownId: string;
  private closeSubscription?: Subscription;

  constructor(
    private dropdownManager: DropdownManagerService,
    private elementRef: ElementRef
  ) {
    // Generate unique ID for this dropdown instance
    this.dropdownId = `user-avatar-${Math.random().toString(36).substr(2, 9)}`;
  }

  ngOnInit(): void {
    // Subscribe to close events from the dropdown manager
    this.closeSubscription = this.dropdownManager.closeAllDropdowns.subscribe(() => {
      this.dropdownOpen = false;
    });
  }

  ngOnDestroy(): void {
    this.dropdownManager.unregisterDropdown(this.dropdownId);
    this.closeSubscription?.unsubscribe();
  }

  get overlayPositions(): ConnectedPosition[] {
    const basePosition = this.rtl
      ? { originX: 'end' as const, overlayX: 'end' as const, offsetX: 0 }
      : { originX: 'start' as const, overlayX: 'start' as const, offsetX: 0 };

    return [
      // Below avatar
      { ...basePosition, originY: 'bottom' as const, overlayY: 'top' as const },
      // Above avatar (if no space below)
      { ...basePosition, originY: 'top' as const, overlayY: 'bottom' as const }
    ];
  }

  get displayIcon(): string {
    if (this.userIcon !== 'fa-user') {
      return this.userIcon;
    }

    if (this.user) {
      if (this.user.Name) {
        return this.removeAccentMarks(`fa-circle-${this.user.Name[0]}`).toLowerCase();
      }
      if (this.user.Username) {
        return this.removeAccentMarks(`fa-circle-${this.user.Username[0]}`).toLowerCase();
      }
    }

    return 'fa-user';
  }

  get iconClass(): string {
    // Check if the icon is a custom kit icon
    if (this.displayIcon.includes('fa-kit')) {
      return this.displayIcon;
    }
    // For regular FontAwesome icons, use 'far fa-fw' prefix
    return `far fa-fw ${this.displayIcon}`;
  }

  get displayName(): string {
    if (this.showMotiLabel) {
      return 'Moti';
    }

    if (this.user) {
      return this.user.Username || this.user.Name || 'User';
    }

    return 'User';
  }

  get fullName(): string {
    if (this.showMotiLabel) {
      return 'Moti AI Assistant';
    }

    if (this.user) {
      return this.user.Name || this.user.Email || this.user.Username || 'User';
    }

    return 'User';
  }


  onAvatarClick(): void {
    if (this.clickable && (this.user || this.showMotiLabel)) {
      if (!this.dropdownOpen) {
        this.dropdownManager.registerDropdown(this.dropdownId);
        this.dropdownOpen = true;
      } else {
        this.dropdownOpen = false;
        this.dropdownManager.unregisterDropdown(this.dropdownId);
      }
    }
  }

  @HostListener('document:click', ['$event'])
  @HostListener('document:touchstart', ['$event'])
  onDocumentClick(event: MouseEvent | TouchEvent): void {
    if (this.dropdownOpen && !this.elementRef.nativeElement.contains(event.target as Node)) {
      this.dropdownOpen = false;
      this.dropdownManager.unregisterDropdown(this.dropdownId);
    }
  }

  private removeAccentMarks(input: string): string {
    return input.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }
}
